import React from 'react';
import PropTypes from 'prop-types';
import FormField from 'components/form/localisation/translations/FormField';
import LocaleDialog from 'components/form/localisation/dialog/LocaleDialog';
import RaisedButton from 'material-ui/RaisedButton';
import TranslateDialog from 'components/form/localisation/dialog/TranslateDialog';
import compose from 'recompose/compose';
import languagesHelper from 'helpers/languages';
import theme from 'theme/theme';
import translate from 'components/hoc/translate';
import translator from 'components/hoc/translator';
import utils from 'helpers/utils';
import { AddIcon, LockIcon } from 'assets/materialIcons/materialIcons';
import { Tab, Tabs } from 'material-ui/Tabs';

const styles = {
    dialog: {
        content: {
            margin: '20px 0',
        },
    },
    button: {
        flex: 1,
    },
    deleteButton: {
        height: '36px',
        maxHeight: '36px',
    },
    list: {
        listStyleType: 'none',
        padding: '0',
        margin: '0',
    },
    listItem: {
        margin: '0 0 30px 0',
        padding: '0',
        width: '80%',
    },
    tab: {
        height: '60px',
        border: `1px solid ${ theme.palette.silver }`,
        borderBottom: 'none',
    },
    tabContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: `1px solid ${ theme.palette.silver }`,
        minHeight: '400px',
    },
    tabContent: {
        margin: '0',
        padding: '20px 0 20px 35px',
    },
    dialogOptions: {
        margin: '20px',
    },
};

class FormTabsWithRequiredFields extends React.Component {
    constructor(props) {
        super(props);

        const firstValue = props.selected[0];

        const systemValuesFilled = this.validateSystemValues();

        this.state = {
            systemValuesFilled: systemValuesFilled,
            showDialog: false,
            showTranslations: false,
            tab: firstValue,
        };
    }

    componentDidUpdate(prevProps, prevState) {
        const { formValues } = this.props;

        if (!formValues) {
            return;
        }

        const newValues = utils.diffObjects(formValues, prevProps.formValues);

        if (newValues) {
            const newResult = this.validateSystemValues();

            if (prevState.systemValuesFilled !== newResult) {
                return this.setState({
                    systemValuesFilled: newResult,
                });
            }
        }
    }

    handleTabs = (value) => {
        if (value === 'addValue') {
            return;
        }

        this.setState({
            tab: value,
        });
    }

    handleRemoveTab = () => {
        const { removeTab, selected } = this.props;
        const { tab } = this.state;

        if (!tab) {
            return true;
        }

        // Send the tab to the parent component to remove from the selected values.
        removeTab(tab);

        // Select the tab next to the deleted tab.
        const value = selected[selected.indexOf(tab) - 1];

        this.setState({
            tab: value,
        });
    }

    /**
     * This will check if all required values are filled in for the system language.
     */
    validateSystemValues = () => {
        const {
            formValues,
            system,
            fields,
        } = this.props;

        if (typeof formValues === 'undefined') {
            return false;
        }

        if (!formValues.hasOwnProperty('localisation')) {
            return false;
        }

        const systemValues = formValues.localisation[system];

        for (let i = 0, max = fields.length; i < max; i++) {
            const field = fields[i];

            if (!field.required) {
                continue;
            }

            if (!utils.get(systemValues, field.name)) {
                return false;
            }
        }

        return true;
    }

    handleOpenDialog = () => {
        this.setState({
            showDialog: true,
        });
    }

    handleSubmit = (locale) => {
        const {
            addTab,
        } = this.props;

        // Switching to the specific tab.
        this.setState({
            showDialog: false,
            showTranslations: true,
            tab: locale,
        }, () => {

            // Add the tab.
            addTab(this.state.tab);
        });
    }

    hideTranslations = () => {
        this.setState({
            showTranslations: false,
        });
    }

    handleTranslationsValues = (locale) => {
        const {
            handleTranslation,
            formValues,
            system,
        } = this.props;

        const systemValues = formValues.localisation[system];

        utils.each(systemValues, function (value, key) {
            handleTranslation(system, locale, value, key);
        });

        this.hideTranslations();
    }

    handleCloseDialog = () => {
        this.setState({
            showDialog: false,
        });
    }

    handleChangeData = (locale) => {
        if (!locale) {
            return;
        }

        this.handleSubmit(locale);
    }

    translatedLabel = (value) => {
        const { translate } = this.props;

        const language = languagesHelper.getLabel(value);

        return translate(`languages.${ language.toLowerCase() }`);
    }

    required = (value) => {
        const { translate } = this.props;

        if (utils.isEmpty(value)) {
            return translate('forms.required');
        }
    }

    maximumLength = (max) => {
        const { translate } = this.props;

        return (value) => {
            if (!utils.isEmpty(value) && (value.length > max)) {
                return translate('forms.invalid.max_characters', { max: max });
            }
        };
    }

    validations = (language, name, required = false) => {
        const {
            fields,
            locked,
            maxLength,
        } = this.props;

        const result = [];
        const field = utils.find(fields, function (field) {
            return field.name === name;
        });

        if (locked === language && required) {
            result.push(this.required);
        }

        result.push(this.maximumLength(field.maxLength || maxLength));

        return result;
    };

    renderAddValues() {
        const { options } = this.props;
        const { systemValuesFilled } = this.state;

        if (!options || !options.length) {
            return null;
        }

        let disabledProps = {};

        if (!systemValuesFilled) {
            disabledProps = {
                icon: <LockIcon hoverColor={ theme.palette.white } />,
                style: {
                    backgroundColor: theme.palette.silver,
                },
            };
        }

        return (
            <Tab
                icon={ <AddIcon hoverColor={ theme.palette.white } /> }
                key='add_value'
                onActive={ this.handleOpenDialog }
                style={ {
                    backgroundColor: 'rgba(220, 247, 251, 1)',
                } }
                value='addValue'
                { ...disabledProps }
            />
        );
    }

    render() {
        const {
            dialogLabel,
            fields,
            formValues,
            locked,
            maxLength,
            minRows,
            options,
            selected,
            system,
            translate,
            updateTranslation,
        } = this.props;

        const {
            showTranslations,
            systemValuesFilled,
            tab,
        } = this.state;

        return (
            <div>
                <Tabs
                    value={ this.state.tab }
                    onChange={ this.handleTabs }
                >
                    {
                        selected.map((locale) => {
                            return (
                                <Tab
                                    key={ `localisation.${ locale }` }
                                    label={ this.translatedLabel(locale) }
                                    style={ styles.tab }
                                    value={ locale }
                                >
                                    <div style={ styles.tabContainer }>
                                        <div style={ styles.tabContent }>
                                            <ul style={ styles.list }>
                                                {
                                                    utils.map(fields, (field) => {
                                                        return (
                                                            <li
                                                                key={ `localisation.${ locale }.${ field.name }` }
                                                                style={ styles.listItem }
                                                            >
                                                                <FormField
                                                                    fieldValidate={ (locale, name) => {
                                                                        return this.validations(locale, name, field.required);
                                                                    } }
                                                                    formValues={ formValues }
                                                                    locale={ locale }
                                                                    maxLength={ field.maxLength || maxLength }
                                                                    minRows={ minRows }
                                                                    name={ field.name }
                                                                    system={ system }
                                                                    type={ field.type }
                                                                    updateTranslation={ updateTranslation }
                                                                />
                                                            </li>
                                                        );
                                                    })
                                                }
                                            </ul>
                                        </div>

                                        <div style={ styles.deleteButton }>
                                            {
                                                (locale !== locked) &&
                                                    <RaisedButton
                                                        fullWidth={ true }
                                                        label={ translate('actions.delete') }
                                                        onTouchTap={ this.handleRemoveTab }
                                                    />
                                            }
                                        </div>
                                    </div>
                                </Tab>
                            );
                        })
                    }

                    { this.renderAddValues() }
                </Tabs>

                {
                    showTranslations &&
                        <TranslateDialog
                            handleSubmit={ (locale) => {
                                this.handleTranslationsValues(locale);
                            } }
                            handleCancel={ () => {
                                this.hideTranslations();
                            } }
                            open={ this.state.showDialog }
                            sourceLanguage={ system }
                            targetLanguage={ tab }
                            formValues={ formValues }
                        />
                }

                <LocaleDialog
                    disabled={ !systemValuesFilled }
                    handleChangeData={ (locale) => {
                        this.handleChangeData(locale);
                    } }
                    handleCloseDialog={ () => {
                        this.handleCloseDialog();
                    } }
                    list={ options }
                    open={ this.state.showDialog }
                    title={ dialogLabel }
                />
            </div>
        );
    }
}

FormTabsWithRequiredFields.defaultProps = {
    maxLength: 325,
    minRows: 6,
};

FormTabsWithRequiredFields.propTypes = {
    addTab: PropTypes.func.isRequired,
    dialogLabel: PropTypes.string.isRequired,
    fields: PropTypes.array,
    formValues: PropTypes.object,
    handleTranslation: PropTypes.func.isRequired,
    locked: PropTypes.string,
    maxLength: PropTypes.number,
    minRows: PropTypes.number,
    options: PropTypes.array,
    removeTab: PropTypes.func.isRequired,
    selected: PropTypes.array,
    system: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    updateTranslation: PropTypes.func.isRequired,
};

const enhance = compose(
    translate,
    translator,
);

export default enhance(FormTabsWithRequiredFields);



// WEBPACK FOOTER //
// ./src/components/form/localisation/FormTabsWithRequiredFields.js